import { useTranslation } from 'react-i18next';
import { BottomSheet } from '@/components/ui/BottomSheet';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { useSchoolCalendar } from '@/services/calendar';
import type { CalendarHoliday, CalendarPeriod } from '@/types/api';

interface Props {
  open: boolean;
  onClose: () => void;
  country: string | undefined;
}

function sortByStart<T extends CalendarPeriod>(periods: T[]): T[] {
  return [...periods].sort((a, b) => a.start.localeCompare(b.start));
}

function PeriodRow({
  name,
  start,
  end,
  dotColor,
  badge,
}: CalendarPeriod & { dotColor: string; badge?: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
        padding: '10px 0',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', minWidth: 0 }}>
        <span
          aria-hidden="true"
          style={{
            width: '10px',
            height: '10px',
            borderRadius: '999px',
            background: dotColor,
            marginTop: '5px',
            flexShrink: 0,
          }}
        />
        <div style={{ minWidth: 0 }}>
          <p style={{ margin: 0, fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-dark)' }}>
            {name}
          </p>
          <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            {start === end ? start : `${start} – ${end}`}
          </p>
        </div>
      </div>
      {badge}
    </div>
  );
}

export function SchoolCalendarSheet({ open, onClose, country }: Props) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useSchoolCalendar(country, open);

  const isPending = data?.status === 'pending';
  const hasData =
    !!data &&
    data.status === 'live' &&
    (data.trimesters.length > 0 || data.holidays.length > 0 || data.national_exam_windows.length > 0);
  const unavailable = !isLoading && !isPending && (isError || !hasData);

  return (
    <BottomSheet open={open} onClose={onClose} title={t('school_calendar.title')}>
      {(isLoading || isPending) && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', padding: '32px 0' }}>
          <Spinner size="lg" className="text-primary-600" />
          <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            {isPending ? t('school_calendar.pending') : t('school_calendar.loading')}
          </p>
        </div>
      )}

      {unavailable && (
        <div style={{ textAlign: 'center', padding: '28px 12px' }}>
          <p style={{ fontSize: '2rem', margin: '0 0 10px' }}>📅</p>
          <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
            {isError ? t('school_calendar.error') : t('school_calendar.empty')}
          </p>
        </div>
      )}

      {!isLoading && !isPending && !unavailable && data && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {data.trimesters.length > 0 && (
            <section>
              <h3 style={{ margin: '0 0 4px', fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-dark)' }}>
                {t('school_calendar.trimesters_heading')}
              </h3>
              {sortByStart(data.trimesters).map((period) => (
                <PeriodRow key={`${period.name}-${period.start}`} {...period} dotColor="var(--primary)" />
              ))}
            </section>
          )}

          {data.holidays.length > 0 && (
            <section>
              <h3 style={{ margin: '0 0 4px', fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-dark)' }}>
                {t('school_calendar.holidays_heading')}
              </h3>
              {sortByStart(data.holidays).map((holiday: CalendarHoliday) => (
                <PeriodRow
                  key={`${holiday.name}-${holiday.start}-${holiday.zone ?? 'all'}`}
                  {...holiday}
                  dotColor="var(--warning)"
                  badge={
                    <Badge
                      label={
                        holiday.zone
                          ? t('school_calendar.zone_label', { zone: holiday.zone })
                          : t('school_calendar.nationwide')
                      }
                      variant="warning"
                    />
                  }
                />
              ))}
            </section>
          )}

          {data.national_exam_windows.length > 0 && (
            <section>
              <h3 style={{ margin: '0 0 4px', fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-dark)' }}>
                {t('school_calendar.exams_heading')}
              </h3>
              {sortByStart(data.national_exam_windows).map((period) => (
                <PeriodRow key={`${period.name}-${period.start}`} {...period} dotColor="var(--danger)" />
              ))}
            </section>
          )}
        </div>
      )}
    </BottomSheet>
  );
}
